import React from 'react';
import {
  string,
  number,
  oneOf,
  bool,
  func,
  arrayOf,
  shape,
  element,
  oneOfType,
} from 'prop-types';
import { Checkbox, Radio } from '@thd-olt-component-react/checkbox';
import './product-add-ons-pod.scss';
import { buildImageURL } from '../../../utils/product-add-on-utils';

const ProductAddOnsPod = ({
  heading,
  subHeading,
  userOptions,
  icon,
  iconHeight,
  onChange,
  channel,
  tooltip,
  displayDisabledText,
  radio,
  children,
}) => {
  const Input = radio ? Radio : Checkbox;
  const handleChange = (evt) => {
    onChange(evt);
  };
  const getDisplayPrice = (price) => {
    if (price === 0) {
      return <span className="product-add-ons-pod__content__checkbox-wrapper__free">&nbsp;FREE</span>;
    }
    if (!price) {
      return null;
    }
    if (typeof price === 'number') {
      return (
        <span className="product-add-ons-pod__content__checkbox-wrapper__label__pricing">
          &nbsp;+&#36;{price.toFixed(2)}
        </span>
      );
    }
    if (typeof price === 'string') {
      return (
        <span className="product-add-ons-pod__content__checkbox-wrapper__label__pricing">
          {price}
        </span>
      );
    }
    return null;
  };
  if (displayDisabledText) {
    return (
      <div className="buybox__add-on product-add-ons-pod">
        <div className="product-add-ons-pod__does-not-apply">
          <div className="product-add-ons-pod__does-not-apply__symbol"> &#33; </div>
          {displayDisabledText}
        </div>
      </div>
    );
  }
  return (
    <div className="buybox__add-on product-add-ons-pod">
      <div className="product-add-ons-pod__content">
        <div className="product-add-ons-pod__icon-wrapper">
          <img
            className="product-add-ons-pod__icon"
            src={buildImageURL(icon)}
            alt=""
            aria-hidden="true"
            style={{ height: `${iconHeight}px` }}
          />
        </div>
        <div>
          <div className="product-add-ons-pod__content__main">
            <span className="product-add-ons-pod__content__main__title">{heading}</span>
            { channel === 'desktop' && tooltip }
          </div>
          <div data-testid="subHeading"> {subHeading} </div>
          {userOptions.length > 0 && (
            <div className="product-add-ons-pod__content__checkbox-wrapper">
              <Input.Group className={`customClass${radio ? 'Radio' : 'Checkbox'}`}>
                {
                  (userOptions || []).map(
                    ({
                      value, checked, label, price
                    }, id) => (
                      <Input
                        id={`${value}__checkbox`}
                        onChange={handleChange}
                        checked={checked}
                        value={value}
                        key={id}
                      >
                        <div
                          className={`product-add-ons-pod__content__checkbox-wrapper__label${radio ? '_radio' : ''}`}
                          data-id={id}
                        >
                          {label}
                          {getDisplayPrice(price)}
                        </div>
                      </Input>
                    ))
                }
              </Input.Group>
            </div>
          )}
          {channel === 'mobile' && tooltip }
        </div>
      </div>
      {children}
    </div>
  );
};

ProductAddOnsPod.displayName = 'ProductAddOns-ProductAddOnsPod';

ProductAddOnsPod.propTypes = {
  /** heading -> Renders as the descriptive title */
  heading: string.isRequired,
  /** subHeading -> Renders as the descriptive sub-title */
  subHeading: string,
  /** icon -> Renders as background image */
  icon: string,
  /** icon height in pixels */
  iconHeight: number,
  /** onChange -> callback ref for user selection event */
  onChange: func,
  /** Populates the tooltip */
  tooltip: element,
  /** userOptions -> list that display input type of checkbox or radio and its label */
  userOptions: arrayOf(shape({
    /** value - current product add on */
    value: string.isRequired,
    /** checkbox state */
    checked: bool.isRequired,
    /** Checkbox Label */
    label: string.isRequired,
    /** Price of the add on */
    price: oneOfType([number, string]),
  })),
  /** radio -> Checkbox is the default type for user options list.  */
  radio: bool,
  /** disabledDisplayText - When this is truthy, display this over default template */
  displayDisabledText: string,
  /** What view to render ['desktop' || 'mobile']  */
  channel: oneOf(['desktop', 'mobile']),
  /** Any element that will be rendered below the existing template */
  children: element,
};

ProductAddOnsPod.defaultProps = {
  subHeading: '',
  channel: 'desktop',
  displayDisabledText: '',
  radio: false,
  userOptions: [],
  tooltip: null,
  icon: '',
  iconHeight: 25,
  children: null,
  onChange: () => {},
};

export { ProductAddOnsPod };
